import React, { useState } from "react"
import {
  Modal,
  Upload,
  Button,
  Input,
  Radio,
  Select,
  Alert,
  Typography,
  Space,
  Tag,
  Divider,
  Spin,
  Table,
  message,
} from "antd"
import { InboxOutlined } from "@ant-design/icons"
import JSZip from "jszip"
import { gen, swimminglyApi, raceTypeArr } from "./utils"
import { EventLabel } from "./ReactDragAndDropTable"
import { IClub, IUser } from "./utils/sharedDataTypes"

const { Dragger } = Upload
const { Paragraph, Text } = Typography

type FileType = "ev3" | "hyv"

interface ExtractedFile {
  name: string
  type: FileType
  content: string
}

interface PreviewResult {
  importedCount: number
  skippedEvents: { lineNumber: number; reason: string; raw: string }[]
  meetName: string | null
  importedEvents: Array<{
    sortOrder: number
    displayNumber: string | null
    ageGroup: string
    gender: string
    distance: number
    raceType: number
    division: string | null
  }>
}

interface SubmitResult {
  eventOrderId: number
  importedCount: number
  skippedEvents: { lineNumber: number; reason: string }[]
}

export interface ImportEventOrderSuccessResult {
  eventOrderId: number
  name: string
  clubId?: number
  leagueId?: number
  entryLimits?: { individual: number | null; relay: number | null; total: number | null }
}

export interface ImportEventOrderModalProps {
  open: boolean
  onClose: () => void
  /** Set when on the club EventOrders page — always creates a club event order. */
  club?: { clubId: number }
  /** Set when on the league EventOrders page — always creates a league event order. */
  league?: { id: number }
  /**
   * Set when opened from CreateMeetModal.
   * Role 4 (club admin): auto-saves to actingUser.clubId.
   * Role 3 (league admin): asks league vs club, club picker uses availableClubsInSeason.
   * Role 2 (swimmingly admin): same as role 3, but actingUser has no leagueId —
   *   pass leagueIdForContext (derived from season clubs) so "League Event Order" resolves correctly.
   */
  actingUser?: IUser | null
  availableClubsInSeason?: IClub[]
  /** League ID for the current season context (needed for role 2 who has no actingUser.leagueId). */
  leagueIdForContext?: number
  onSuccess: (result: ImportEventOrderSuccessResult) => void
}

type Step = "upload" | "fileselect" | "preview" | "done"

/**
 * Determines how ownership is handled for the import.
 *   "club"   — auto-determined, no choice shown
 *   "league" — auto-determined, no choice shown
 *   "ask"    — league admin / swimmingly admin: show radio + optional club picker
 */
function resolveOwnershipMode(
  club?: ImportEventOrderModalProps["club"],
  league?: ImportEventOrderModalProps["league"],
  actingUser?: IUser | null,
): "club" | "league" | "ask" {
  if (club) return "club"
  if (league) return "league"
  if (actingUser?.role === 4) return "club"
  return "ask"
}

export default function ImportEventOrderModal({
  open,
  onClose,
  club,
  league,
  actingUser,
  availableClubsInSeason,
  leagueIdForContext,
  onSuccess,
}: ImportEventOrderModalProps) {
  const [step, setStep] = useState<Step>("upload")
  const [extractedFiles, setExtractedFiles] = useState<ExtractedFile[]>([])
  const [selectedFile, setSelectedFile] = useState<ExtractedFile | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null)
  const [eventOrderName, setEventOrderName] = useState("")
  const [ownerType, setOwnerType] = useState<"club" | "league">("league")
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)

  const ownershipMode = resolveOwnershipMode(club, league, actingUser)

  const resetState = () => {
    setStep("upload")
    setExtractedFiles([])
    setSelectedFile(null)
    setPreviewing(false)
    setPreviewResult(null)
    setEventOrderName("")
    setOwnerType("league")
    setSelectedClubId(null)
    setSubmitting(false)
    setSubmitResult(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const fetchPreview = async (file: ExtractedFile) => {
    setPreviewing(true)
    try {
      const data: any = await swimminglyApi
        .post(gen("/api/previewEventOrderImport"))
        .body({ fileContent: file.content, fileType: file.type })
      if (data?.status === "success") {
        setPreviewResult({
          importedCount: data.importedCount,
          skippedEvents: data.skippedEvents || [],
          meetName: data.meetName || null,
          importedEvents: data.importedEvents || [],
        })
        setEventOrderName(data.meetName || "Imported Event Order")
      } else {
        message.error("Could not parse the event file.")
      }
    } catch {
      message.error("Error contacting the server.")
    } finally {
      setPreviewing(false)
    }
  }

  const processFile = async (file: ExtractedFile) => {
    setSelectedFile(file)
    await fetchPreview(file)
    setStep("preview")
  }

  const handleFileUpload = async (file: File): Promise<false> => {
    const lower = file.name.toLowerCase()
    try {
      if (lower.endsWith(".zip")) {
        const zip = await JSZip.loadAsync(file)
        const found: ExtractedFile[] = []
        for (const [zipName, entry] of Object.entries(zip.files)) {
          if (entry.dir) continue
          const lz = zipName.toLowerCase()
          if (lz.endsWith(".ev3") || lz.endsWith(".hyv")) {
            const content = await entry.async("string")
            found.push({
              name: zipName,
              type: lz.endsWith(".ev3") ? "ev3" : "hyv",
              content,
            })
          }
        }
        if (found.length === 0) {
          message.error("No EV3 or HYV files found inside the ZIP.")
          return false
        }
        // Sort: EV3 first (recommended), then HYV
        found.sort((a, b) => {
          if (a.type === "ev3" && b.type !== "ev3") return -1
          if (b.type === "ev3" && a.type !== "ev3") return 1
          return a.name.localeCompare(b.name)
        })
        if (found.length === 1) {
          await processFile(found[0])
        } else {
          setExtractedFiles(found)
          setSelectedFile(found[0]) // pre-select EV3
          setStep("fileselect")
        }
      } else if (lower.endsWith(".ev3")) {
        const content = await file.text()
        await processFile({ name: file.name, type: "ev3", content })
      } else if (lower.endsWith(".hyv")) {
        const content = await file.text()
        await processFile({ name: file.name, type: "hyv", content })
      } else {
        message.error("Please upload a .zip, .ev3, or .hyv file.")
      }
    } catch {
      message.error("Failed to read the file. Please try again.")
    }
    return false
  }

  const handleFileselectNext = async () => {
    if (!selectedFile) return
    await fetchPreview(selectedFile)
    setStep("preview")
  }

  const getOwnerIds = (): { clubId?: number; leagueId?: number } => {
    if (club) return { clubId: club.clubId }
    if (league) return { leagueId: league.id }
    if (ownershipMode === "club") return { clubId: actingUser?.clubId ?? undefined }
    // "ask" mode (league admin role 3, or swimmingly admin role 2)
    if (ownerType === "league") {
      const resolvedLeagueId = actingUser?.leagueId ?? leagueIdForContext
      return { leagueId: resolvedLeagueId }
    }
    if (ownerType === "club" && selectedClubId) return { clubId: selectedClubId }
    return {}
  }

  const canSubmit = (): boolean => {
    if (!eventOrderName.trim() || !selectedFile || !previewResult) return false
    if (previewResult.importedCount === 0) return false
    const { clubId, leagueId } = getOwnerIds()
    if (!clubId && !leagueId) return false
    if (ownershipMode === "ask" && ownerType === "club" && !selectedClubId) return false
    return true
  }

  const handleSubmit = async () => {
    if (!selectedFile || !canSubmit()) return
    const { clubId, leagueId } = getOwnerIds()
    setSubmitting(true)
    try {
      const data: any = await swimminglyApi
        .post(gen("/api/importEventOrderFromFile"))
        .body({
          fileContent: selectedFile.content,
          fileType: selectedFile.type,
          name: eventOrderName.trim(),
          clubId,
          leagueId,
        })
      if (data?.status === "success") {
        const result: SubmitResult = {
          eventOrderId: data.eventOrderId,
          importedCount: data.importedCount,
          skippedEvents: data.skippedEvents || [],
        }
        setSubmitResult(result)
        setStep("done")
        onSuccess({
          eventOrderId: data.eventOrderId,
          name: eventOrderName.trim(),
          clubId: clubId ?? undefined,
          leagueId: leagueId ?? undefined,
          entryLimits: data.entryLimits ?? undefined,
        })
      } else {
        message.error("Import failed. Please try again.")
      }
    } catch {
      message.error("Error contacting the server.")
    } finally {
      setSubmitting(false)
    }
  }

  // --- Modal title ---
  const titleMap: Record<Step, string> = {
    upload: "Import Event Order from File",
    fileselect: "Select Event File",
    preview: "Review & Confirm Import",
    done: "Import Complete",
  }

  // --- Modal footer ---
  const footer =
    step === "upload" ? (
      <Button onClick={handleClose}>Cancel</Button>
    ) : step === "fileselect" ? (
      <Space>
        <Button onClick={() => setStep("upload")}>Back</Button>
        <Button type="primary" onClick={handleFileselectNext} disabled={!selectedFile}>
          Next
        </Button>
      </Space>
    ) : step === "preview" ? (
      <Space>
        <Button
          onClick={() => setStep(extractedFiles.length > 1 ? "fileselect" : "upload")}
        >
          Back
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!canSubmit()}
          loading={submitting}
        >
          Import {previewResult?.importedCount ?? 0} Event
          {previewResult?.importedCount !== 1 ? "s" : ""}
        </Button>
      </Space>
    ) : (
      <Button type="primary" onClick={handleClose}>
        Close
      </Button>
    )

  // --- Step bodies ---
  const uploadBody = (
    <Dragger
      accept=".zip,.ev3,.hyv"
      beforeUpload={handleFileUpload}
      showUploadList={false}
      multiple={false}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">Click or drag your event file here</p>
      <p className="ant-upload-hint">
        Accepts a <strong>.zip</strong> from Hy-Tek MEET MANAGER, or a bare{" "}
        <strong>.ev3</strong> / <strong>.hyv</strong> file.
      </p>
    </Dragger>
  )

  const fileselectBody = (
    <Space orientation="vertical" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        title="Multiple event files found — EV3 is recommended."
        description="EV3 includes full gender detail (Girls, Boys, Women, Men, Mixed). HYV can only represent Men, Women, and Mixed."
        style={{ marginBottom: 8 }}
      />
      <Radio.Group
        value={selectedFile?.name}
        onChange={(e) => {
          const f = extractedFiles.find((x) => x.name === e.target.value)
          if (f) setSelectedFile(f)
        }}
      >
        <Space orientation="vertical" style={{ width: "100%" }}>
          {extractedFiles.map((f) => (
            <Radio key={f.name} value={f.name}>
              <Space>
                <Text>{f.name}</Text>
                <Tag color={f.type === "ev3" ? "green" : "blue"}>
                  {f.type.toUpperCase()}
                </Tag>
                {f.type === "ev3" && <Tag color="gold">Recommended</Tag>}
              </Space>
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </Space>
  )

  const previewBody = previewing ? (
    <div style={{ textAlign: "center", padding: 32 }}>
      <Spin description="Parsing file…" size="large" />
    </div>
  ) : (
    <Space orientation="vertical" style={{ width: "100%" }} size={16}>
      {/* HYV gender limitation notice */}
      {selectedFile?.type === "hyv" && (
        <Alert
          type="warning"
          showIcon
          title="HYV files use Men/Women/Mixed only — Girls and Boys will be imported as Women and Men respectively. Consider using the EV3 file if available."
        />
      )}

      {/* Parse summary */}
      {previewResult && (
        <Alert
          type={previewResult.importedCount > 0 ? "success" : "warning"}
          showIcon
          title={
            previewResult.importedCount > 0
              ? `${previewResult.importedCount} event${previewResult.importedCount !== 1 ? "s" : ""} ready to import${
                  previewResult.skippedEvents.length > 0
                    ? ` · ${previewResult.skippedEvents.length} will be skipped`
                    : ""
                }`
              : "No importable events found in this file."
          }
        />
      )}

      {/* Imported events preview table */}
      {previewResult && previewResult.importedEvents.length > 0 && (() => {
        const hasDivision = previewResult.importedEvents.some((e) => e.division)
        const previewColumns: any[] = [
          {
            title: "Event #",
            key: "eventNum",
            render: (_: any, record: any) => (
              <EventLabel
                sortOrder={record.sortOrder}
                displayNumber={record.displayNumber}
              />
            ),
          },
          { title: "Age Group", dataIndex: "ageGroup", key: "ageGroup" },
          { title: "Gender", dataIndex: "gender", key: "gender" },
          { title: "Distance", dataIndex: "distance", key: "distance" },
          {
            title: "Stroke",
            key: "stroke",
            render: (_: any, record: any) => (raceTypeArr as any)[record.raceType] ?? record.raceType,
          },
          ...(hasDivision
            ? [{ title: "Division", dataIndex: "division", key: "division", render: (v: any) => v || "—" }]
            : []),
        ]
        return (
          <div style={{ maxHeight: 420, overflow: "auto", border: "1px solid #f0f0f0", borderRadius: 4 }}>
            <Table
              size="small"
              dataSource={previewResult.importedEvents}
              columns={previewColumns}
              pagination={false}
              rowKey={(_, i) => String(i)}
            />
          </div>
        )
      })()}

      {/* Skipped events detail */}
      {(previewResult?.skippedEvents.length ?? 0) > 0 && (
        <details style={{ marginTop: -8 }}>
          <summary style={{ cursor: "pointer", color: "#faad14", fontSize: 13 }}>
            View {previewResult!.skippedEvents.length} skipped line
            {previewResult!.skippedEvents.length !== 1 ? "s" : ""}
          </summary>
          <ul style={{ marginTop: 6, fontSize: 12, color: "#888", paddingLeft: 20 }}>
            {previewResult!.skippedEvents.map((s, i) => (
              <li key={i}>
                Line {s.lineNumber}: {s.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Divider style={{ margin: "4px 0" }} />

      {/* Event order name */}
      <div>
        <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
          Event Order Name
        </label>
        <Input
          value={eventOrderName}
          onChange={(e) => setEventOrderName(e.target.value)}
          placeholder="e.g. Dual Meet 2026"
          maxLength={100}
        />
      </div>

      {/* Ownership — league admin / swimmingly admin */}
      {ownershipMode === "ask" && (
        <>
          <div>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>
              Save As
            </label>
            <Radio.Group
              value={ownerType}
              onChange={(e) => {
                setOwnerType(e.target.value)
                setSelectedClubId(null)
              }}
            >
              <Space orientation="vertical">
                <Radio value="league">
                  League Event Order{" "}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (available to all clubs in the league)
                  </Text>
                </Radio>
                <Radio value="club">
                  Club Event Order{" "}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (saved to a specific club)
                  </Text>
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          {ownerType === "club" && (
            <div>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                Select Club
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder="Choose a club…"
                value={selectedClubId ?? undefined}
                onChange={(id) => setSelectedClubId(id)}
                showSearch={{
                  filterOption: (input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                }}
                options={(availableClubsInSeason || []).map((c) => ({
                  value: c.clubId,
                  label: c.nameLong || (c as any).name_long || c.name || `Club ${c.clubId}`,
                }))}
              />
            </div>
          )}
        </>
      )}

      {/* Informational note for club admin context */}
      {ownershipMode === "club" && (
        <Alert
          type="info"
          showIcon
          title="This event order will be saved to your club and available for future meets. League admins can create league-level event orders from the League Event Orders screen."
        />
      )}

      {/* Informational note for league context */}
      {ownershipMode === "league" && (
        <Alert
          type="info"
          showIcon
          title="This event order will be saved as a league event order, available to all clubs in the league."
        />
      )}
    </Space>
  )

  const doneBody = (
    <Space orientation="vertical" style={{ width: "100%" }} size={12}>
      <Alert
        type="success"
        showIcon
        title={`${submitResult?.importedCount ?? 0} event${(submitResult?.importedCount ?? 0) !== 1 ? "s" : ""} imported successfully.`}
        description={
          (submitResult?.skippedEvents.length ?? 0) > 0
            ? `${submitResult!.skippedEvents.length} event${submitResult!.skippedEvents.length !== 1 ? "s" : ""} could not be imported and were skipped. You can add them manually in the event order editor.`
            : "All events were imported successfully."
        }
      />
      {(submitResult?.skippedEvents.length ?? 0) > 0 && (
        <details>
          <summary style={{ cursor: "pointer", color: "#faad14", fontSize: 13 }}>
            View skipped events
          </summary>
          <ul style={{ marginTop: 6, fontSize: 12, color: "#888", paddingLeft: 20 }}>
            {submitResult!.skippedEvents.map((s, i) => (
              <li key={i}>
                {"lineNumber" in s && (s as any).lineNumber
                  ? `Line ${(s as any).lineNumber}: `
                  : ""}
                {s.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Space>
  )

  const bodyMap: Record<Step, React.ReactNode> = {
    upload: uploadBody,
    fileselect: fileselectBody,
    preview: previewBody,
    done: doneBody,
  }

  return (
    <Modal
      open={open}
      title={titleMap[step]}
      onCancel={handleClose}
      footer={footer}
      width={520}
      destroyOnHidden
    >
      {bodyMap[step]}
    </Modal>
  )
}
